import { useCallback, useEffect, useMemo, useState } from "react";
import { api, STATUS_LABELS } from "@/lib/api";
import { ArrowsClockwise, MagnifyingGlass, Desktop, UserCircle, Package, Stack, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";
import HintButton from "@/components/HintButton";

const DAYS_OPTIONS = [
  { value: 1, label: "Hoje" },
  { value: 7, label: "7 dias" },
  { value: 30, label: "30 dias" },
  { value: 90, label: "90 dias" },
];

const KIND_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "kanban_move", label: "Kanban" },
  { value: "inventory_move", label: "Estoque" },
];

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [kind, setKind] = useState("");
  const [station, setStation] = useState("");
  const [collab, setCollab] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (kind) params.kind = kind;
      if (station) params.station = station;
      if (collab) params.collaborator = collab;
      const [{ data }, { data: s }] = await Promise.all([
        api.get("/audit/movements", { params }),
        api.get("/audit/stats", { params: { days } }),
      ]);
      setRows(Array.isArray(data) ? data : []);
      setStats(Array.isArray(s) ? s : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar log");
    } finally {
      setLoading(false);
    }
  }, [days, kind, station, collab]);

  useEffect(() => { load(); }, [load]);

  const stations = useMemo(() => {
    const set = new Set(rows.map((r) => r.station_name).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => {
      return (
        (r.bling_number || "").toLowerCase().includes(q) ||
        (r.customer || "").toLowerCase().includes(q) ||
        (r.collaborator_name || "").toLowerCase().includes(q) ||
        (r.collaborator_code || "").toLowerCase().includes(q) ||
        (r.station_name || "").toLowerCase().includes(q) ||
        (r.type_label || "").toLowerCase().includes(q) ||
        (r.color || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  const statsByStation = useMemo(() => {
    const map = {};
    stats.forEach((r) => {
      if (!map[r.station]) map[r.station] = { kanban_move: 0, inventory_move: 0 };
      map[r.station][r.kind] = r.count;
    });
    return map;
  }, [stats]);

  return (
    <div className="p-6 md:p-10" data-testid="audit-log-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500 mb-2">AUDITORIA</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Log de Apontamentos</h1>
          <HintButton text="Registro imutável de cada movimentação feita no sistema: quem fez (colaborador com código) e onde fez (estação/computador da fábrica)." />
        </div>
        <button className="btn btn-ghost" onClick={load} data-testid="audit-refresh">
          <ArrowsClockwise size={16} weight="bold" /> Atualizar
        </button>
      </div>

      {/* Stats por estação */}
      {Object.keys(statsByStation).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="audit-stats">
          {Object.entries(statsByStation).map(([st, counts]) => (
            <div key={st || "sem-estacao"} className="border border-black bg-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <Desktop size={16} weight="bold" />
                <div className="font-head font-bold">{st || "Sem estação"}</div>
              </div>
              <div className="text-xs text-zinc-500 flex gap-4">
                <span><strong className="text-black font-mono">{counts.kanban_move || 0}</strong> Kanban</span>
                <span><strong className="text-black font-mono">{counts.inventory_move || 0}</strong> Estoque</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4" data-testid="audit-filters">
        <div>
          <label className="label">Período</label>
          <select className="input" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="audit-days">
            {DAYS_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div>
          <label className="label">Tipo</label>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="audit-kind">
            {KIND_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div>
          <label className="label">Estação</label>
          <select className="input" value={station} onChange={(e) => setStation(e.target.value)} data-testid="audit-station">
            <option value="">Todas</option>
            {stations.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
        </div>
        <div>
          <label className="label">Colaborador (código ou nome)</label>
          <input className="input font-mono" placeholder="ex: 100 ou Bruno" value={collab} onChange={(e) => setCollab(e.target.value)} data-testid="audit-collab" />
        </div>
        <div>
          <label className="label">Busca livre</label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input className="input pl-9" placeholder="pedido, cliente, cor..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="audit-search" />
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="border border-black bg-white overflow-x-auto" data-testid="audit-list">
        {loading ? (
          <div className="text-zinc-400 py-10 text-center">Carregando…</div>
        ) : filteredRows.length === 0 ? (
          <div className="text-zinc-400 py-10 text-center">Nenhuma movimentação encontrada.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Data / Hora</th>
                <th>Tipo</th>
                <th>Detalhe</th>
                <th>Colaborador</th>
                <th>Estação</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                  <td className="font-mono text-xs whitespace-nowrap">{fmtDateTime(r.at)}</td>
                  <td>
                    {r.kind === "kanban_move" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest bg-zinc-900 text-white px-2 py-1">
                        <Package size={12} weight="bold" /> Kanban
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest bg-yellow-500 text-black px-2 py-1">
                        <Stack size={12} weight="bold" /> Estoque
                      </span>
                    )}
                  </td>
                  <td className="text-sm">
                    {r.kind === "kanban_move" ? (
                      <div>
                        <div className="font-mono font-bold">#{r.bling_number}</div>
                        <div className="text-xs text-zinc-500 truncate max-w-[240px]">{r.customer || "—"}</div>
                        <div className="text-[11px] mt-1">
                          <span className="text-zinc-500">{STATUS_LABELS[r.from_status] || r.from_status}</span>
                          <span className="mx-1">→</span>
                          <span className="font-semibold text-[#00A300]">{STATUS_LABELS[r.to_status] || r.to_status}</span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="font-semibold">{r.type_label} · <span className="font-mono">{r.color}</span></div>
                        <div className={`text-xs font-bold ${r.delta > 0 ? "text-[#00A300]" : "text-[#E60000]"}`}>
                          {r.delta > 0 ? "+" : ""}{r.delta} un · saldo {r.stock_after}
                        </div>
                        {r.note && <div className="text-[11px] text-zinc-500 mt-1 truncate max-w-[240px]">{r.note}</div>}
                      </div>
                    )}
                  </td>
                  <td className="text-sm">
                    <div className="flex items-center gap-2">
                      <UserCircle size={16} weight="bold" />
                      <div>
                        <div className="font-semibold">{r.collaborator_name || "—"}</div>
                        <div className="text-[11px] text-zinc-500 font-mono">
                          cod {r.collaborator_code || "—"}
                          {r.collaborator_sector && <> · {r.collaborator_sector}</>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="text-sm">
                    {r.station_name ? (
                      <div className="flex items-center gap-2">
                        <Desktop size={16} weight="bold" />
                        <div>
                          <div className="font-semibold">{r.station_name}</div>
                          {r.station_sector && <div className="text-[11px] text-zinc-500">{r.station_sector}</div>}
                        </div>
                      </div>
                    ) : (
                      <span className="text-zinc-400 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filteredRows.length > 0 && (
        <div className="text-xs text-zinc-500 mt-3 flex items-center gap-2">
          <ClipboardText size={12} />
          Exibindo <span className="font-mono font-bold text-black mx-1">{filteredRows.length}</span> movimentação(ões) dos últimos <span className="font-mono font-bold text-black mx-1">{days}</span> dia(s).
        </div>
      )}
    </div>
  );
}
